import { defineStore } from 'pinia'
import type {
  SegmentFragment,
  Track,
  RatioTypeUnion,
  SegmentNoGroupCollection,
} from '@ve-pro/types/track.type'
import { useTrackStore } from '../tracks'
import { useReplaceStore } from '../replace'
import { cloneDeep } from 'lodash-es'
import { updateMainTrack } from '../tracks/update-main-track'
import {
  defineSegmentProperties,
  hasSegmentMaterial,
} from '../tracks/resolve-segment'
import { updateTransitionRelevance } from '../tracks/segment/segment-transition'
import { isSegmentGroup } from '../tracks/segment/segment-group'
import { useProjectStore } from '../project'
import { ProjectMode, ProjectTab } from '@ve-pro/enums/project'
import { Recorder } from './recorder'
import type { RecorderOptions, TracksDiff } from './recorder'
import { TrackMode, TrackType } from '@ve-pro/enums/track-type'
import {
  getDefaultActiveSegment,
  setActiveSegment,
} from '../tracks/toggle-active-segment'
import { isSegmentFragment } from '../tracks/segment/segment-fragment'
import { shallowReactive } from 'vue'
import { ReplaceMode } from '@ve-pro/enums/replace'
import { ProjectConfig } from '@ve-pro/api/project/type'
import { TaskStatus } from '@ve-pro/replace/components/task-manager/enums'

type HistoryValue = {
  project_tab: ProjectTab
  project_mode: ProjectMode
  track_mode: TrackMode
  value: Track[]
  activeSegmentFragmentId?: SegmentFragment['id']
  ratio: RatioTypeUnion
  ratios: Array<RatioTypeUnion>
  isRatioLinkage: boolean
  replace_mode: ReplaceMode
  project_languages: string[]
  activeLanguage: ProjectConfig['activeLanguage']
  mainRatio: ProjectConfig['mainRatio']
  languagesReplaceAssetIds: ProjectConfig['languagesReplaceAssetIds']
  segmentGroups: SegmentNoGroupCollection[]
  combineIds: SegmentFragment['id'][]
  enableLanguagesSegmentIds: SegmentFragment['id'][]
  changedDefaultLanguagesSegmentsIds: SegmentFragment['id'][]
  changedSegmentsIds: SegmentFragment['id'][]
  enableAllSubtitleLanguages: boolean
  speaker: ProjectConfig['speaker']
}
type HistoryDiff = TracksDiff<HistoryValue>

const prefilter: RecorderOptions<any>['prefilter'] = (path, key) => {
  return ['cuttingDuration', 'before', 'behind'].includes(key)
}

const MAX = 30
export const useHistoryStore = defineStore('history', {
  state: () => {
    const projectStore = useProjectStore()
    const trackStore = useTrackStore()
    const replaceStore = useReplaceStore()

    return {
      // 最大记录数
      max: MAX,

      recorder: new Recorder<HistoryValue>(
        () => ({
          project_tab: projectStore.tab,
          project_mode: projectStore.mode,
          track_mode: trackStore.mode,
          activeSegmentFragmentId: trackStore.activeSegmentFragment?.id ?? 0,
          value: trackStore.tracks,
          ratio: projectStore.ratio,
          ratios: [...projectStore.ratios],
          isRatioLinkage: projectStore.configs.isRatioLinkage,
          languagesReplaceAssetIds: [
            ...projectStore.configs.languagesReplaceAssetIds,
          ],
          replace_mode: replaceStore.mode,
          project_languages: [...projectStore.languages],
          activeLanguage: projectStore.configs.activeLanguage,
          mainRatio: projectStore.configs.mainRatio,
          segmentGroups: Object.values(trackStore.segmentGroups).reduce(
            (acc, curr) => [...acc, ...curr],
            [],
          ),
          combineIds: [...projectStore.configs.combineIds],
          changedDefaultLanguagesSegmentsIds: [
            ...projectStore.configs.changedDefaultLanguagesSegmentsIds,
          ],
          enableLanguagesSegmentIds: [
            ...projectStore.configs.enableLanguagesSegmentIds,
          ],
          enableAllSubtitleLanguages:
            projectStore.configs.enableAllSubtitleLanguages,
          changedSegmentsIds: [...projectStore.configs.changedSegmentsIds],
          speaker: projectStore.configs.speaker,
        }),
        {
          max: MAX,
          prefilter,
        },
      ),

      // 历史记录
      history: [] as HistoryDiff[],
      // 可还原的历史记录
      poped: [] as HistoryDiff[],
    }
  },
  getters: {
    isDisabled() {
      const projectStore = useProjectStore()

      const hasAiTasks = projectStore.configs.task.some(
        (t) => t.status !== TaskStatus.Success,
      )

      return hasAiTasks
    },
  },
  actions: {
    init() {
      this.save()
    },
    save() {
      const recorder = this.recorder
      recorder.save()
    },

    push() {
      if (this.isDisabled) {
        return
      }
      this.recorder.push()
      syncHistory()
    },

    undo() {
      if (this.isDisabled) {
        return
      }
      this.recorder.undo()
      syncMode()
      syncHistory()

      syncTracks()
    },

    redo() {
      if (this.isDisabled) {
        return
      }
      this.recorder.redo()
      syncMode()
      syncHistory()

      syncTracks()
    },

    /**
     * 恢复到最新保存的状态
     */
    revert() {
      if (this.isDisabled) {
        return
      }
      syncMode()
      syncTracks()
    },

    /**
     * clear history
     */
    clear() {
      if (this.isDisabled) {
        return
      }
      this.recorder.reset()
      syncHistory()
    },
  },
})

function syncHistory() {
  const historyStore = useHistoryStore()
  const recorder = historyStore.recorder

  historyStore.history = shallowReactive(
    recorder.history.slice(0, recorder.current + 1),
  )
  historyStore.poped = shallowReactive(
    recorder.history.slice(recorder.current + 1),
  )
}

function syncMode() {
  const historyStore = useHistoryStore()
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()
  const replaceStore = useReplaceStore()

  const state: HistoryValue = historyStore.recorder.savedState

  // 1.同步轨道模式
  trackStore.mode = state.track_mode
  projectStore.mode = state.project_mode
  projectStore.tab = state.project_tab

  // 多尺寸
  projectStore.ratio = state.ratio
  projectStore.ratios = [...state.ratios]
  projectStore.configs.isRatioLinkage = state.isRatioLinkage

  // 视频模版模式
  replaceStore.mode = state.replace_mode

  // 多语言
  projectStore.languages = [...state.project_languages]
  projectStore.configs.activeLanguage = state.activeLanguage
  projectStore.configs.mainRatio = state.mainRatio
  projectStore.configs.languagesReplaceAssetIds = [
    ...state.languagesReplaceAssetIds,
  ]
  projectStore.configs.combineIds = [...state.combineIds]
  projectStore.configs.changedDefaultLanguagesSegmentsIds = [
    ...state.changedDefaultLanguagesSegmentsIds,
  ]
  projectStore.configs.enableLanguagesSegmentIds = [
    ...state.enableLanguagesSegmentIds,
  ]
  projectStore.configs.changedSegmentsIds = [...state.changedSegmentsIds]
  projectStore.configs.enableAllSubtitleLanguages =
    state.enableAllSubtitleLanguages
  projectStore.configs.speaker = cloneDeep(state.speaker)
}

function syncTracks() {
  const historyStore = useHistoryStore()
  const trackStore = useTrackStore()
  const projectStore = useProjectStore()

  const state: HistoryValue = historyStore.recorder.savedState

  // 2.同步剧本
  trackStore.tracks = defineAllSegmentProperties(cloneDeep(state.value))
  if (
    trackStore.defaultExcludeFragmentMainTrack &&
    trackStore.defaultExcludeFragmentMainTrack.segments.length > 0
  ) {
    const mainTracks: Track[] = [
      trackStore.defaultExcludeFragmentMainTrack,
      ...(trackStore.defaultExcludeFragmentMainTrack?.segments
        .map((s) => {
          if (isSegmentFragment(s)) {
            return trackStore.getMainTrack(
              trackStore.getTracksByFragmentSegmentId(s.id),
            )
          }

          return null
        })
        .filter((m): m is Track<TrackType.Video> => Boolean(m)) ?? []),
    ]

    mainTracks.forEach((mainTrack) => {
      updateTransitionRelevance(mainTrack)
      updateMainTrack(true, {
        mainTrack,
      })
    })
  }

  // 3.设置当前片段
  trackStore.activeSegmentFragment = state.activeSegmentFragmentId
    ? trackStore.segments.find(
        (s): s is SegmentFragment =>
          isSegmentFragment(s) && s.id === state.activeSegmentFragmentId,
      ) ?? null
    : null

  syncActiveSegment()

  if (projectStore.isReplaceProject) {
    const segments = cloneDeep(state.segmentGroups).map((s) =>
      defineSegmentProperties(s, s.source_rate),
    )

    segments.forEach((s) => {
      const group = trackStore.segmentGroups[s.pid] ?? []
      const index = group.findIndex((g) => g.id === s.id)
      if (index > -1) {
        group.splice(index, 1, s)
      } else {
        // group.push(s)
      }
    })
  }
}

function syncActiveSegment() {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()
  const act = trackStore.segments.find(
    (seg) =>
      seg.id === trackStore.activeSegment?.id &&
      trackStore.activeSegment.stage === projectStore.mode,
  )
  trackStore.activeSegment = act ?? null
  if (act) {
    const segments = trackStore.segments.filter((s) =>
      trackStore.activeSegments.includes(s.id),
    )
    const ids = segments.map((s) => s.id)
    const target = getDefaultActiveSegment(ids)
    setActiveSegment(target, ids)
  } else {
    setActiveSegment()
  }

  if (trackStore.activeSegmentGroup) {
    const target =
      trackStore.segments.find(
        (s) => s.id === trackStore.activeSegmentGroup?.id,
      ) ?? null

    trackStore.activeSegmentGroup =
      target as typeof trackStore.activeSegmentGroup
  } else {
    trackStore.activeSegmentGroup = null
  }
}
function defineAllSegmentProperties(tracks: Track[]): Track[] {
  const projectStore = useProjectStore()

  tracks.forEach((t) => {
    t.segments.forEach((s) => {
      if (isSegmentGroup(s)) {
        const isExist = projectStore.assetGroups.some(
          (group) => group.id === s.asset_group_id,
        )
        s.deprecated = s.deprecated || !isExist
      } else {
        // true 表示被删除了
        const isExist = projectStore.deletedAssets.some(
          (item) =>
            hasSegmentMaterial(s) && item.asset_id === s.material?.asset_id,
        )
        const isDeleted = hasSegmentMaterial(s) && s.material ? isExist : false

        s.deprecated = s.deprecated || isDeleted
      }
      defineSegmentProperties(s, s.source_rate)
    })
  })
  return tracks
}
